import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { attachmentStorageKey } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { pictoForAsset, portraitsByAsset, type AssetLookInput } from '../data/assetLook.ts'
import type { BlobRow } from '../sync/db.ts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import type { Transport } from '../sync/transport.ts'
import { Picto, PictoBadge } from '../ui/pictos.tsx'
import { fitWithin } from './capture.ts'
import { useBlobs, type LinkRow } from './usePhotos.ts'

/** A machine's portrait (M17): the newest photo linked to it with the purpose 'photo'. A list of thirty machines must not decode thirty 1600 px photos, nor ask storage for them on
 *  every visit: each portrait is reduced once on the device to the size it is shown at and kept in the browser's cache under its hash (a content-addressed file never changes), so the
 *  shed sees the pictures with no signal. No portrait yet, or not reachable: the machine's pictogram. */
export function usePortraits(): Map<string, Pending<LinkRow>> {
  const links = useMerged<LinkRow>('attachment_link', 'core.attachment_link')
  return useMemo(() => portraitsByAsset(links ?? []), [links])
}

const SIDES = { sm: 240, lg: 1200 } as const
type Size = keyof typeof SIDES
const CACHE = 'gk-portraits-v1'
const made = new Map<string, Promise<string | null>>()           // one reduction per picture and size in a session, shared by every row that shows it
let running = 0
const waiting: Array<() => void> = []
async function limited<T>(fn: () => Promise<T>): Promise<T> {     // a few storage requests at a time, not thirty
  if (running >= 4) await new Promise<void>((r) => waiting.push(r))
  running++
  try { return await fn() } finally { running--; waiting.shift()?.() }
}

async function reduce(blob: Blob, side: number): Promise<Blob> {
  const bitmap = await createImageBitmap(blob)
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, side)
    const canvas = document.createElement('canvas')
    canvas.width = width; canvas.height = height
    const g = canvas.getContext('2d')
    if (!g) return blob
    g.drawImage(bitmap, 0, 0, width, height)
    return await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b ?? blob), 'image/jpeg', 0.82))
  } finally { bitmap.close() }
}

function portraitUrl(transport: Transport, storageKey: string, sha: string, size: Size, local: Blob | undefined): Promise<string | null> {
  const id = `${sha}-${size}`
  const have = made.get(id)
  if (have) return have
  const p = (async () => {
    const key = `/__portrait/${id}.jpg`
    const cache = typeof caches === 'undefined' ? null : await caches.open(CACHE).catch(() => null)
    const hit = await cache?.match(key)
    if (hit) return URL.createObjectURL(await hit.blob())
    const source = local ?? await limited(async () => {
      const r = await fetch(await transport.attachmentUrl(storageKey))
      if (!r.ok) throw new Error(`portrait ${r.status}`)
      return r.blob()
    })
    const small = await reduce(source, SIDES[size])
    await cache?.put(key, new Response(small, { headers: { 'content-type': 'image/jpeg' } })).catch(() => undefined)
    return URL.createObjectURL(small)
  })().catch(() => { made.delete(id); return null })               // offline, or not uploaded yet: try again on the next visit
  made.set(id, p)
  return p
}

export function MachinePortrait({ asset, link, blob, size = 'sm', className }: { asset: AssetLookInput; link: Pending<LinkRow> | undefined; blob?: BlobRow; size?: Size; className?: string }) {
  const { t } = useTranslation()
  const { transport, org } = useClub()
  const [url, setUrl] = useState<string | null>(null)
  const sha = link?.sha256
  useEffect(() => {
    setUrl(null)
    if (!link || !sha) return
    let live = true
    void portraitUrl(transport, attachmentStorageKey(org.org_id, link.site_id, sha, link.mime), sha, size, blob?.blob).then((u) => { if (live) setUrl(u) })
    return () => { live = false }
  }, [sha, link?.site_id, link?.mime, blob?.blob, size, transport, org.org_id])
  const box = className ?? (size === 'sm' ? 'size-16 rounded-xl' : 'aspect-[4/3] w-full rounded-2xl')
  if (url) return <img src={url} alt={t('assets.portraitAlt')} className={`${box} shrink-0 border border-slate-200 bg-white object-cover dark:border-night-line`} data-testid="asset-portrait" data-sha={sha} />
  if (size === 'sm') return <span className={`${box} turf-soft inline-flex shrink-0 items-center justify-center border border-fairway/30 text-brand dark:border-night-line dark:text-fairway`} data-testid="asset-picto" data-picto={pictoForAsset(asset)}><Picto name={pictoForAsset(asset)} className="size-9" strokeWidth={1.5} /></span>
  return (
    <span className={`${box} turf-soft flex flex-col items-center justify-center gap-2 border border-slate-200 text-center dark:border-night-line`} data-testid="asset-picto" data-picto={pictoForAsset(asset)}>
      <PictoBadge name={pictoForAsset(asset)} size="lg" />
      <span className="px-4 text-sm opacity-70">{link ? t('assets.portraitLoading') : t('assets.portraitNone')}</span>
    </span>
  )
}

/** The portrait of one machine, with this device's copy of a photo that is still waiting to be sent. */
export function usePortrait(assetId: string): { link: Pending<LinkRow> | undefined; blob: BlobRow | undefined } {
  const portraits = usePortraits()
  const blobs = useBlobs()
  const link = portraits.get(assetId)
  return { link, blob: link ? blobs.get(link.sha256) : undefined }
}
